/**
 * Transaction Service
 *
 * API client for transaction-related endpoints.
 */

import { fetchApi } from '@/shared/services/api';
import type {
  TransactionRead,
  TransactionWithCategory,
  TransactionCreate,
  TransactionUpdate,
  ReimbursementMark,
  ReimbursementAccept,
  NotificationRead,
  SettlementRead,
  ExpenseAllocationRead,
  ExpenseAllocationWithUser,
  ExpenseAllocationsUpdate,
  ReceiptRead,
  ReceiptUploadResponse,
  PaginatedResponse,
  SplitType,
  AuditLogWithUser,
} from '@/shared/types';

export interface TransactionListParams {
  account_id?: string;
  category_id?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
  transaction_type?: 'debit' | 'credit';
  split_type?: SplitType;
  is_pending?: boolean;
  excluded_from_totals?: boolean;
  is_uncategorized?: boolean;
  min_amount?: number;
  max_amount?: number;
  search?: string;
  page?: number;
  page_size?: number;
  sort?: string;
}

export const transactionService = {
  /**
   * List transactions with optional filters
   */
  list: (params?: TransactionListParams) =>
    fetchApi<PaginatedResponse<TransactionWithCategory>>('/transactions', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get a single transaction by ID
   */
  getById: (id: string) =>
    fetchApi<TransactionWithCategory>(`/transactions/${id}`),

  /**
   * Create a new transaction
   */
  create: (data: TransactionCreate) =>
    fetchApi<TransactionRead>('/transactions', { method: 'POST', body: data }),

  /**
   * Update a transaction
   */
  update: (id: string, data: TransactionUpdate) =>
    fetchApi<TransactionRead>(`/transactions/${id}`, { method: 'PATCH', body: data }),

  /**
   * Delete a transaction
   */
  delete: (id: string) =>
    fetchApi<void>(`/transactions/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Allocations
  // =========================================================================

  /**
   * Get expense allocations for a transaction
   */
  getAllocations: (id: string) =>
    fetchApi<PaginatedResponse<ExpenseAllocationWithUser>>('/expense-allocations', {
      params: { transaction_id: id },
    }).then((res) => res.items),

  /**
   * Set expense allocations for a transaction (bulk update)
   */
  setAllocations: (id: string, data: ExpenseAllocationsUpdate) =>
    fetchApi<ExpenseAllocationRead[]>(`/expense-allocations/bulk/${id}`, {
      method: 'POST',
      body: data,
    }),

  // =========================================================================
  // Receipts
  // =========================================================================

  /**
   * Get receipts for a transaction
   */
  getReceipts: (id: string) =>
    fetchApi<ReceiptRead[]>(`/transactions/${id}/receipts`),

  /**
   * Upload a receipt for a transaction
   */
  uploadReceipt: (id: string, file: File): Promise<ReceiptUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    return fetchApi<ReceiptUploadResponse>(`/transactions/${id}/receipts`, {
      method: 'POST',
      body: formData,
    });
  },

  /**
   * Delete a receipt
   */
  deleteReceipt: (transactionId: string, receiptId: string) =>
    fetchApi<void>(`/transactions/${transactionId}/receipts/${receiptId}`, { method: 'DELETE' }),

  // =========================================================================
  // Reimbursement
  // =========================================================================

  /**
   * Mark a transaction as a reimbursement (Phase 1 — sends notification to recipient)
   */
  markReimbursement: (transactionId: string, data: ReimbursementMark) =>
    fetchApi<NotificationRead>(`/transactions/${transactionId}/reimbursement`, {
      method: 'POST',
      body: data,
    }),

  /**
   * Accept a reimbursement request (Phase 2 — links to recipient's transaction, creates settlement)
   */
  acceptReimbursement: (transactionId: string, data: ReimbursementAccept) =>
    fetchApi<SettlementRead>(`/transactions/${transactionId}/reimbursement/accept`, {
      method: 'POST',
      body: data,
    }),

  /**
   * Decline a reimbursement request (reverts sender's transaction)
   */
  declineReimbursement: (transactionId: string) =>
    fetchApi<void>(`/transactions/${transactionId}/reimbursement/decline`, {
      method: 'POST',
    }),

  /**
   * Undo a reimbursement marking on a transaction (sender cancels)
   */
  undoReimbursement: (transactionId: string) =>
    fetchApi<void>(`/transactions/${transactionId}/reimbursement`, {
      method: 'DELETE',
    }),

  // =========================================================================
  // Activity
  // =========================================================================

  /**
   * Get audit activity timeline for a transaction
   */
  getActivity: (id: string) =>
    fetchApi<AuditLogWithUser[]>(`/transactions/${id}/activity`),
};
